import React, { createContext, useContext, useState } from 'react';

const SearchContext = createContext();

export const useSearchContext = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearchContext must be used within a SearchProvider');
  }
  return context;
};

export const SearchProvider = ({ children }) => {
  // Location states
  const [from, setFrom] = useState(["Los Angeles (LAX)"]);
  const [to, setTo] = useState(["San Francisco (SFO)"]);
  
  // Get tomorrow and day after tomorrow dates
  const getTomorrow = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const getDayAfterTomorrow = () => {
    const dayAfterTomorrow = new Date();
    dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
    return dayAfterTomorrow.toISOString().split('T')[0];
  };

  // Date states
  const [departureDate, setDepartureDate] = useState(getTomorrow());
  const [returnDate, setReturnDate] = useState(getDayAfterTomorrow());
  
  // Flight segments for multi-city
  const [flightSegments, setFlightSegments] = useState([
    {
      id: 1,
      from: "London (LON)",
      to: "Manchester (MAN)",
      date: getTomorrow(),
      cabin: "Economy",
    }
  ]);
  
  // Passenger states
  const [adults, setAdults] = useState(1);
  const [students, setStudents] = useState(0);
  const [seniors, setSeniors] = useState(0);
  const [youths, setYouths] = useState(0);
  const [childrenCount, setChildrenCount] = useState(0);
  const [toddlers, setToddlers] = useState(0);
  const [infants, setInfants] = useState(0);
  
  // Bag states
  const [carryOnBags, setCarryOnBags] = useState(0);
  const [checkedBags, setCheckedBags] = useState(0);
  
  // Trip configuration
  const [tripType, setTripType] = useState("Round-trip");
  const [cabin, setCabin] = useState("Economy");
  const [directOnly, setDirectOnly] = useState(true);
  const [compareKayak, setCompareKayak] = useState(false);
  const [includeSouthwest, setIncludeSouthwest] = useState(false);
  
  // Date picker states
  const [datePopoverAnchor, setDatePopoverAnchor] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date(getTomorrow())); // Initialize to show departure date month
  const [dateTab, setDateTab] = useState(0);
  const [departureExact, setDepartureExact] = useState("exact");
  const [returnExact, setReturnExact] = useState("exact");
  const [activeSegmentId, setActiveSegmentId] = useState(null);
  const [activeDateField, setActiveDateField] = useState(null);
  
  // Weekend and month modes
  const [weekendSelectedDate, setWeekendSelectedDate] = useState(null);
  const [weekendDeparture, setWeekendDeparture] = useState('thursday');
  const [weekendReturn, setWeekendReturn] = useState('sunday');
  const [isWeekendMode, setIsWeekendMode] = useState(false);
  const [monthDuration, setMonthDuration] = useState('6-8');
  const [isMonthMode, setIsMonthMode] = useState(false);
  
  // UI refs
  const searchBarRef = React.useRef(null);
  const passengerButtonRef = React.useRef(null);

  // Dropdown states for Autocomplete
  const [fromFieldOpen, setFromFieldOpen] = useState(false);
  const [toFieldOpen, setToFieldOpen] = useState(false);

  // Filter states
  const [trackPrices, setTrackPrices] = useState(false);
  const [bookOnKayak, setBookOnKayak] = useState(false);
  const [timeFilter, setTimeFilter] = useState("takeoff");
  const [smartFilterText, setSmartFilterText] = useState("");
  const [carryOnCount, setCarryOnCount] = useState(0);
  const [checkedBagCount, setCheckedBagCount] = useState(0);
  const [stopsFilter, setStopsFilter] = useState([]);
  const [airlinesFilter, setAirlinesFilter] = useState([]);
  const [allianceFilter, setAllianceFilter] = useState([]);
  const [airportsFilter, setAirportsFilter] = useState([]);
  const [layoverAirportsFilter, setLayoverAirportsFilter] = useState([]);
  const [cabinFilter, setCabinFilter] = useState([]);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 10000 });
  const [amenitiesFilter, setAmenitiesFilter] = useState([]);

  // Flight quality filters
  const [flightQuality, setFlightQuality] = useState({
    wifiOnly: false,
    redEyeOnly: false,
    layoverTransfersOnly: false,
  });
  
  // Time filter states
  const [timeRanges, setTimeRanges] = useState({
    atl: { start: 8.62, end: 13.92 }, // 8:37 AM to 1:55 PM in decimal hours
    abq: { start: 7.93, end: 17.02 }  // 7:56 AM to 5:01 PM in decimal hours
  });
  
  // Duration filter states
  const [durationRanges, setDurationRanges] = useState({
    // Start at 0 to avoid filtering out short flights in dataset; UI still shows 1h20m label
    flightLeg: { start: 0, end: 1677 },
    layover: { start: 35, end: 1424 }
  });
  
  // Search results and filtering
  const [searchResults, setSearchResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [availableFilters, setAvailableFilters] = useState({
    airlines: [],
    stops: [],
    cabins: [],
    amenities: [],
    layoverAirports: [],
    priceRange: { min: 0, max: 0 },
    stopsPrices: {}
  });
  
  // Sort state
  const [activeSort, setActiveSort] = useState("best"); // "cheapest", "best", "quickest"

  const value = {
    // Location states
    from, setFrom,
    to, setTo,
    
    // Date states
    departureDate, setDepartureDate,
    returnDate, setReturnDate,
    
    // Flight segments
    flightSegments, setFlightSegments,
    
    // Passenger states
    adults, setAdults,
    students, setStudents,
    seniors, setSeniors,
    youths, setYouths,
    childrenCount, setChildrenCount,
    toddlers, setToddlers,
    infants, setInfants,
    
    // Bag states
    carryOnBags, setCarryOnBags,
    checkedBags, setCheckedBags,
    
    // Trip configuration
    tripType, setTripType,
    cabin, setCabin,
    directOnly, setDirectOnly,
    compareKayak, setCompareKayak,
    includeSouthwest, setIncludeSouthwest,
    
    // Date picker states
    datePopoverAnchor, setDatePopoverAnchor,
    currentMonth, setCurrentMonth,
    dateTab, setDateTab,
    departureExact, setDepartureExact,
    returnExact, setReturnExact,
    activeSegmentId, setActiveSegmentId,
    activeDateField, setActiveDateField,
    
    // Weekend and month modes
    weekendSelectedDate, setWeekendSelectedDate,
    weekendDeparture, setWeekendDeparture,
    weekendReturn, setWeekendReturn,
    isWeekendMode, setIsWeekendMode,
    monthDuration, setMonthDuration,
    isMonthMode, setIsMonthMode,
    
    // UI refs
    searchBarRef,
    passengerButtonRef,
    
    // Dropdown states
    fromFieldOpen, setFromFieldOpen,
    toFieldOpen, setToFieldOpen,
    
    // Filter states
    trackPrices, setTrackPrices,
    bookOnKayak, setBookOnKayak,
    timeFilter, setTimeFilter,
    smartFilterText, setSmartFilterText,
    carryOnCount, setCarryOnCount,
    checkedBagCount, setCheckedBagCount,
    stopsFilter, setStopsFilter,
    airlinesFilter, setAirlinesFilter,
    allianceFilter, setAllianceFilter,
    airportsFilter, setAirportsFilter,
    layoverAirportsFilter, setLayoverAirportsFilter,
    cabinFilter, setCabinFilter,
    priceRange, setPriceRange,
    amenitiesFilter, setAmenitiesFilter,
    flightQuality, setFlightQuality,
    
    // Time filter states
    timeRanges, setTimeRanges,
    
    // Duration filter states
    durationRanges, setDurationRanges,
    
    // Search results and filtering
    searchResults, setSearchResults,
    filteredResults, setFilteredResults,
    availableFilters, setAvailableFilters,
    
    // Sort state
    activeSort, setActiveSort,
  };

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
}; 